import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { LEGAL_DOCS } from '@/features/legal/content';

type Row = { to: string; label: string; sub: string; icon: ReactNode; accent?: 'gold' };

const I = {
  ticket: <><path d="M4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8z" /><path d="M10 6v12" strokeDasharray="2 2" /></>,
  piggy: <><rect x="5" y="10.5" width="14" height="9.5" rx="2.5" /><path d="M8.5 10.5V8a3.5 3.5 0 1 1 7 0v2.5" /><circle cx="12" cy="15" r="1.3" /></>,
  deposit: <><rect x="3" y="6" width="18" height="13" rx="2.5" /><path d="M3 10h18M12 13.5v3M10.5 15h3" /></>,
  shield: <><path d="M12 3 4 6v5.5c0 4.6 3.2 8.4 8 9.5 4.8-1.1 8-4.9 8-9.5V6l-8-3Z" /></>,
  receipt: <><path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z" /><path d="M9 8h6M9 12h6" /></>,
  gift: <><rect x="3" y="8" width="18" height="4" rx="1" /><path d="M5 12v8h14v-8M12 8v12M12 8c-2-3-6-3-6 0s4 0 6 0c2-3 6-3 6 0s-4 0-6 0" /></>,
  scale: <><path d="M12 3v18M4 7h16M6 7l-3 7a3 3 0 0 0 6 0L6 7zM18 7l-3 7a3 3 0 0 0 6 0l-3-7" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>,
  gear: <><circle cx="12" cy="12" r="3.2" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" /></>,
};

export function AccountMenu({ isStaff }: { isStaff: boolean }) {
  const rows: Row[] = [
    { to: '/bets/history', label: 'My bets', sub: 'Every bet, win/loss record, current streak', icon: I.ticket },
    { to: '/savings', label: 'Savings & goal', sub: 'Locked Savings, lock tranches, set a savings goal, withdraw', icon: I.piggy },
    { to: '/deposit', label: 'Deposit', sub: 'Add funds, identity verification', icon: I.deposit },
    { to: '/limits', label: 'Limits & controls', sub: 'Deposit and loss limits · Take a break · Self-exclusion', icon: I.shield },
    { to: '#activity', label: 'Activity statement', sub: 'Deposits, withdrawals and fees paid — on this page below', icon: I.receipt },
    { to: '#referral', label: 'Refer a friend', sub: 'Your referral code', icon: I.gift },
    { to: '/legal', label: 'Legal & policies', sub: LEGAL_DOCS.map((d) => d.title.replace(/ Policy$| & Conditions$/, '')).slice(0, 4).join(' · ') + ' · …', icon: I.scale },
    { to: 'mailto:support@puntsave.com.au', label: 'Help & support', sub: 'support@puntsave.com.au', icon: I.info },
  ];
  if (isStaff) rows.push({ to: '/admin', label: 'Staff console', sub: 'Alerts · params · markets · users · reports', icon: I.gear, accent: 'gold' });

  return (
    <nav className="rounded-2xl border border-white/[0.08] bg-brand-surface/70 divide-y divide-white/[0.06] overflow-hidden" aria-label="Account sections">
      {rows.map((r) => {
        const inner = (
          <>
            <span className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${r.accent === 'gold' ? 'bg-brand-gold/10 text-brand-gold' : 'bg-brand-teal/10 text-brand-teal'}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">{r.icon}</svg>
            </span>
            <span className="min-w-0 flex-1">
              <span className={`block text-sm font-semibold ${r.accent === 'gold' ? 'text-brand-gold' : ''}`}>{r.label}</span>
              <span className="block text-xs text-white/45 truncate">{r.sub}</span>
            </span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-white/30 shrink-0"><path d="m9 6 6 6-6 6" /></svg>
          </>
        );
        const cls = 'flex items-center gap-3 px-4 py-3 hover:bg-white/[0.04] transition';
        return r.to.startsWith('mailto:') || r.to.startsWith('#')
          ? <a key={r.label} href={r.to} className={cls}>{inner}</a>
          : <Link key={r.label} to={r.to} className={cls}>{inner}</Link>;
      })}
    </nav>
  );
}
